import React from 'react';
import { ShieldCheck, Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import { SUPPORT } from '../lyphline';

// Full LyphLine Warranty & Support Policy (owner-supplied wording).
const SECTIONS = [
  {
    id: 'retrofit-systems',
    title: 'LyphLine Freeze Dryer Retrofit Systems',
    blocks: [
      { p: 'LyphLine specializes in repairing, upgrading, and retrofitting existing freeze dryers. Because a retrofit combines new LyphLine components and workmanship with portions of an existing machine, our warranty is designed to clearly distinguish between the work and components supplied by LyphLine and the original components that remain in the machine.' },
      { p: 'Our goal is straightforward: if something we supplied or installed fails because of a covered defect, we will work with you to diagnose the problem and provide an appropriate solution.' },
    ],
  },
  {
    id: '90-day-warranty',
    title: '90-Day Limited Retrofit Warranty',
    blocks: [
      { p: 'Unless a longer warranty is specifically stated in writing with your purchase, LyphLine retrofit equipment and installation services include a:' },
      { strong: '90-Day Limited Parts and Labour Warranty' },
      { p: 'The warranty period begins when the completed machine is delivered, picked up, or otherwise returned to service following completion of the LyphLine retrofit.' },
      { p: 'The 90-day warranty covers qualifying defects in:' },
      { ul: [
        'Components supplied and installed by LyphLine;',
        'Components specifically replaced by LyphLine;',
        'Electrical and control work performed by LyphLine;',
        'Refrigeration work performed by LyphLine;',
        'Mechanical, sensing, heating, vacuum, or other work specifically included in the retrofit; and',
        'LyphLine workmanship associated with the documented repair or retrofit.',
      ] },
      { p: 'Coverage is limited to the components and work identified as part of the LyphLine service.' },
    ],
  },
  {
    id: 'what-it-means',
    title: 'What Does the 90-Day Warranty Mean?',
    blocks: [
      { p: "The 90-day warranty is LyphLine's standard voluntary warranty on covered retrofit parts and workmanship." },
      { p: 'Depending on where you live, consumer-protection, sale-of-goods, state, provincial, territorial, or other laws may provide additional rights beyond this written warranty.' },
      { p: 'Nothing in this policy is intended to remove or restrict any legal right that cannot lawfully be waived or limited.' },
    ],
  },
  {
    id: 'original-warranty',
    title: 'What Happens to the Original Freeze Dryer Warranty?',
    blocks: [
      { p: 'A LyphLine retrofit does not automatically create a new warranty covering every original component in the freeze dryer.' },
      { p: 'Freeze dryers received for retrofit may contain components that are:' },
      { ul: ['Several years old;', 'Previously repaired;', 'Worn;', 'Modified;', 'Contaminated;', 'Near the end of their service life; or', 'Manufactured by another company.'] },
      { p: "Where an original component remains in the machine, LyphLine's inspection or testing of that component means only that it appeared reasonably serviceable at the time of the retrofit. It is not a guarantee of the remaining life of that original component." },
      { p: 'For example, an original vacuum pump, compressor, chamber, heater, door, shelf, refrigeration component, or other retained part does not automatically become covered simply because another portion of the freeze dryer was retrofitted.' },
      { p: 'However, if a covered LyphLine component or defective LyphLine workmanship directly causes damage to an otherwise serviceable retained component, that circumstance will be reviewed as part of the warranty claim.' },
    ],
  },
  {
    id: 'covered-claim',
    title: 'What We Will Do for a Covered Warranty Claim',
    blocks: [
      { p: 'If a problem is determined to be covered under the warranty, LyphLine may provide an appropriate remedy such as:' },
      { ul: [
        'Repairing the covered component;',
        'Replacing the covered component;',
        'Correcting or reperforming covered workmanship;',
        'Providing a replacement or equivalent component;',
        'Authorizing an appropriate repair by a qualified technician; or',
        'Where appropriate, providing a credit or refund relating to the affected covered work or component.',
      ] },
      { p: 'Replacement components may be new, remanufactured, reconditioned, upgraded, superseding, or functionally equivalent parts.' },
      { p: 'The appropriate solution will depend on the nature of the problem, safety considerations, location, component availability, and applicable law.' },
    ],
  },
  {
    id: 'claims-support',
    title: 'Warranty Claims & Technical Support',
    blocks: [
      { p: 'Warranty claims and service requests can be submitted through our online support portal:' },
      { link: SUPPORT.url, linkText: 'www.freezedryersupport.com' },
      { p: 'The support portal is the recommended way to submit a claim because it allows our technical team to track the machine, photographs, videos, diagnostic information, and service history in one location.' },
      { p: 'You may also contact LyphLine directly:' },
      { ul: ['Toll-Free: 1-888-223-1435', 'Email: info@lyphlinefreezedry.com', 'Service Location: Lethbridge, Alberta, Canada'] },
      { p: 'If the online portal is unavailable, you may submit your request by phone or email.' },
      { p: 'Submitting a service request does not automatically mean that the issue has been approved as a warranty claim. LyphLine may need to perform troubleshooting, testing, inspection, or diagnostics before determining coverage.' },
    ],
  },
  {
    id: 'info-needed',
    title: 'Information We May Need',
    blocks: [
      { p: 'To help us diagnose your machine, please be prepared to provide information such as:' },
      { ul: ['Your name or company name;', 'Machine make and model;', 'Machine serial number;', 'LyphLine retrofit or service number;', 'Invoice or work order;', 'Date of retrofit;', 'Description of the problem;', 'Photographs;', 'Video;', 'Error or alarm messages;', 'Cycle information;', 'Operating data; and', 'Proof of purchase or service.'] },
      { p: 'An original paper receipt is not required where we can reasonably verify your purchase or service through another reliable record.' },
    ],
  },
  {
    id: 'remote-diagnostics',
    title: 'Remote Diagnostics',
    blocks: [
      { p: 'Many freeze-dryer problems can be diagnosed without immediately transporting the entire machine.' },
      { p: 'Depending on the issue, LyphLine may use:' },
      { ul: ['Telephone support;', 'Online technical support;', 'Photographs and video;', 'Machine operating information;', 'Remote diagnostic information;', 'Component testing;', 'Local service technicians; or', 'Return of a particular component.'] },
      { p: 'Customers should not perform hazardous electrical, refrigeration, pressure, vacuum, or mechanical procedures unless appropriately qualified to perform that work.' },
    ],
  },
  {
    id: 'not-covered',
    title: 'What Is Not Normally Covered?',
    blocks: [
      { p: 'The LyphLine 90-day limited warranty does not normally cover failures or damage caused by circumstances outside the covered retrofit work, including:' },
      { ul: [
        'Normal wear and tear;',
        'Failure of original components not supplied or replaced by LyphLine;',
        'Pre-existing damage or deterioration;',
        'Corrosion or contamination not caused by LyphLine;',
        'Improper operation;',
        'Failure to follow operating instructions;',
        'Improper or insufficient maintenance;',
        'Improper cleaning;',
        'Overloading;',
        'Abuse, misuse, neglect, or accidental damage;',
        'Incorrect electrical supply;',
        'Voltage fluctuations, surges, brownouts, or improper grounding;',
        'Incorrect refrigerant, oil, chemicals, or replacement components;',
        'Flood, fire, lightning, freezing, pests, or other external events;',
        "Transportation or physical damage occurring after the machine leaves LyphLine's control;",
        'Improper third-party repairs or modifications;',
        'Unauthorized programming or control changes that cause or contribute to a failure;',
        'Operation after a known fault, leak, alarm, overheating condition, electrical problem, or vacuum failure where continued use causes additional damage; or',
        'Removal or alteration of identification or service information where warranty eligibility can no longer reasonably be verified.',
      ] },
      { p: 'An excluded event does not automatically void the entire warranty. Coverage may be denied only to the extent that an excluded condition, modification, component, repair, or event caused or contributed to the claimed failure.' },
    ],
  },
  {
    id: 'third-party',
    title: 'Can Someone Else Work on My Freeze Dryer?',
    blocks: [
      { p: 'Yes, but we strongly recommend contacting LyphLine before another company modifies or repairs a system that is still within its LyphLine warranty period.' },
      { p: 'Having another technician work on the machine does not automatically cancel all warranty coverage.' },
      { p: 'However, LyphLine is not responsible for damage caused by:' },
      { ul: ['Incorrect third-party repairs;', 'Incorrect wiring;', 'Improper refrigeration work;', 'Incompatible replacement parts;', 'Incorrect programming;', 'Improper installation; or', 'Other work that causes or contributes to the failure.'] },
      { p: 'This approach allows customers to obtain service while protecting LyphLine from responsibility for damage caused by work performed by others.' },
    ],
  },
  {
    id: 'manufacturer-warranty',
    title: 'Original Manufacturer Warranty',
    blocks: [
      { p: 'A LyphLine retrofit may affect warranty coverage offered by the original freeze dryer manufacturer.' },
      { p: "Customers should review their original manufacturer's warranty before authorizing retrofit work." },
      { p: 'LyphLine does not determine whether another manufacturer must continue to provide warranty coverage and does not guarantee how another manufacturer will interpret its warranty.' },
    ],
  },
  {
    id: 'refrigeration',
    title: 'Refrigeration Components',
    blocks: [
      { p: 'Where LyphLine replaces or modifies refrigeration components as part of the documented retrofit, the components and workmanship supplied by LyphLine are covered under the applicable LyphLine warranty.' },
      { p: 'Original refrigeration components that remain in the machine are not automatically covered.' },
      { p: 'Certain compressors, valves, pumps, motors, controllers, or other components may also include separate manufacturer warranties. Where practical, LyphLine may assist customers in identifying applicable manufacturer coverage.' },
    ],
  },
  {
    id: 'results',
    title: 'Freeze-Drying Results',
    blocks: [
      { p: 'Our warranty covers the machine retrofit, not the value or outcome of the product being freeze dried.' },
      { p: 'Freeze-drying performance can be affected by many factors, including:' },
      { ul: ['Product type;', 'Product thickness;', 'Preparation;', 'Loading;', 'Moisture content;', 'Recipe;', 'Shelf temperature;', 'Vacuum performance;', 'Ambient temperature;', 'Maintenance;', 'Operator settings; and', 'Condition of original machine components.'] },
      { p: 'Unless specifically guaranteed in a separate written agreement, LyphLine does not guarantee:' },
      { ul: ['A specific cycle time;', 'A specific production rate;', 'Finished moisture content;', 'Product taste or appearance;', 'Shelf life;', 'Product yield; or', 'Successful processing of a particular batch.'] },
    ],
  },
  {
    id: 'downtime',
    title: 'Product Loss, Downtime & Business Interruption',
    blocks: [
      { p: 'Freeze dryers can be used to process valuable products, including food, pet products, floral products, nutraceuticals, laboratory materials, agricultural products, cannabis products, and other commercial materials.' },
      { p: "LyphLine does not insure the contents placed inside a customer's freeze dryer." },
      { p: 'To the maximum extent permitted by applicable law, the limited warranty does not include compensation for indirect or consequential losses such as:' },
      { ul: ['Spoiled or lost product;', 'Lost production;', 'Business interruption;', 'Machine downtime;', 'Lost revenue;', 'Lost profits;', 'Lost sales or contracts;', 'Customer penalties;', 'Replacement-machine rental;', 'Unrelated labour;', 'Travel;', 'Accommodation; or', 'Other indirect commercial losses.'] },
      { p: 'Commercial customers should consider whether backup equipment, product-loss insurance, inventory controls, or other risk-management measures are appropriate for their operation.' },
      { p: 'Nothing in this policy excludes liability that cannot legally be excluded or limited.' },
    ],
  },
  {
    id: 'shipping',
    title: 'Shipping & Transportation',
    blocks: [
      { p: 'Depending on the type of repair, LyphLine may:' },
      { ul: ['Ship replacement components;', 'Repair and return a component;', 'Provide remote support;', 'Authorize a qualified local technician;', 'Ask for the machine to be transported to a service location; or', 'Arrange on-site service where available.'] },
      { p: 'Unless otherwise included in an extended service plan or required by applicable law, customers may be responsible for costs associated with transporting a complete machine to a LyphLine service location.' },
      { p: 'Extraordinary freight, expedited freight, special packaging, customs, brokerage, duties, tariffs, travel, and on-site service are not automatically included in the standard warranty.' },
      { p: 'For an accepted warranty claim where LyphLine elects to repair or replace an individual covered component, LyphLine will ordinarily provide standard outbound shipping of the repaired or replacement covered component to the customer.' },
    ],
  },
  {
    id: 'warranty-time',
    title: 'Warranty Time While We Have Your Machine',
    blocks: [
      { p: 'If LyphLine takes possession of your machine or covered component for authorized warranty work, the remaining voluntary warranty period will be extended by the period the machine or covered component is in our possession for that warranty work.' },
      { p: 'Additional extensions required by applicable law will also apply.' },
    ],
  },
  {
    id: 'noise',
    title: 'Noise & Normal Machine Operation',
    blocks: [
      { p: 'Freeze dryers are mechanical refrigeration and vacuum systems.' },
      { p: 'Normal operation may include:' },
      { ul: ['Vacuum pump noise;', 'Compressor noise;', 'Fan noise;', 'Valve sounds;', 'Clicking;', 'Vibration;', 'Heat;', 'Airflow; and', 'Changing sounds during different stages of the cycle.'] },
      { p: 'Noise by itself is not considered a warranty defect unless it is caused by an abnormal operating condition or a covered defective component.' },
    ],
  },
  {
    id: 'extended',
    title: 'Extended Warranty, Protection & Support Options',
    blocks: [
      { p: 'The standard LyphLine retrofit includes the applicable 90-day limited warranty unless otherwise stated.' },
      { p: 'Customers may also have the option to purchase additional protection, support, or service programs.' },
      { p: 'Depending on the plan selected, additional services may include:' },
      { ul: ['Extended parts coverage;', 'Extended labour coverage;', 'Priority technical support;', 'Remote diagnostics;', 'Preventative maintenance;', 'Firmware or software support;', 'Refrigeration support;', 'Vacuum-system support;', 'Preferred scheduling;', 'Discounted replacement components; and', 'Other enhanced service options.'] },
      { p: 'Any separately purchased extended protection, service, or support plan is governed by the terms provided with that specific plan.' },
      { p: 'Purchasing an extended plan is not required to receive coverage under the standard 90-day LyphLine warranty.' },
    ],
  },
  {
    id: 'after-warranty',
    title: 'Support After the Warranty Ends',
    blocks: [
      { p: 'The end of the 90-day warranty does not mean LyphLine support ends.' },
      { p: 'We can continue to provide:' },
      { ul: ['Technical support;', 'Diagnostics;', 'Repairs;', 'Replacement components;', 'Software and control support;', 'Refrigeration service;', 'Vacuum-system service;', 'Maintenance;', 'Upgrades; and', 'Other technical services.'] },
      { p: 'Services performed outside an applicable warranty or extended service plan may be subject to parts, labour, diagnostic, shipping, travel, or other service charges.' },
    ],
  },
  {
    id: 'canadian',
    title: 'Canadian Customers',
    blocks: [
      { p: 'Canadian customers may have statutory warranties, implied conditions, consumer-protection rights, sale-of-goods rights, or other remedies under applicable provincial, territorial, or federal law.' },
      { p: "These legal rights may exist independently of LyphLine's 90-day voluntary limited warranty." },
      { p: 'Nothing in this policy is intended to waive, reduce, replace, or restrict a right or remedy that cannot lawfully be excluded.' },
    ],
  },
  {
    id: 'quebec',
    title: 'Québec Customers',
    blocks: [
      { p: 'Québec consumers have additional rights under Québec law, including legal warranties relating to the use and reasonable durability of qualifying goods and services.' },
      { p: 'Where required, LyphLine will provide applicable consumer warranty information and documentation in French.' },
      { p: 'Additional or extended warranty products offered to Québec consumers may also be subject to specific disclosure and cancellation requirements.' },
    ],
  },
  {
    id: 'us',
    title: 'United States Customers',
    blocks: [
      { p: 'For qualifying U.S. consumer transactions, this warranty is intended to operate as a:' },
      { strong: '90-Day Limited Warranty' },
      { p: 'Applicable federal and State law may provide rights in addition to the express coverage described in this policy.' },
      { p: 'Some States do not allow the exclusion or limitation of incidental or consequential damages, so certain limitations described above may not apply to you.' },
      { p: 'This warranty gives you specific legal rights, and you may also have other rights which vary from State to State.' },
      { p: 'Nothing in this policy limits rights that cannot lawfully be restricted under applicable federal or State law.' },
    ],
  },
  {
    id: 'commercial',
    title: 'Commercial & Industrial Customers',
    blocks: [
      { p: 'Commercial and industrial customers may be subject to additional terms contained in:' },
      { ul: ['Quotations;', 'Work orders;', 'Commercial service agreements;', 'Purchase agreements;', 'Extended service agreements; or', 'Other written contracts.'] },
      { p: 'Where a separately signed commercial agreement contains warranty, liability, service-level, or risk-allocation provisions specific to a transaction, those provisions may supplement this general website policy to the extent permitted by law.' },
    ],
  },
  {
    id: 'documentation',
    title: 'Warranty Documentation',
    blocks: [
      { p: 'Customers should retain:' },
      { ul: ['Purchase invoices;', 'Work orders;', 'Retrofit documentation;', 'Service records;', 'Delivery records;', 'Extended protection agreements; and', 'Documentation relating to subsequent repairs or modifications.'] },
      { p: 'Good service records help us diagnose equipment faster and determine which components are covered.' },
    ],
  },
];

const Block = ({ b }) => {
  if (b.strong) return <p className="text-[#003456] font-bold text-lg my-3">{b.strong}</p>;
  if (b.p) return <p className="text-slate-600 leading-relaxed my-3">{b.p}</p>;
  if (b.ul) return (
    <ul className="my-3 space-y-1.5">
      {b.ul.map((li, i) => (
        <li key={i} className="flex items-start gap-2 text-slate-600 text-[15px]">
          <span className="text-[#CE8946] mt-1.5 flex-shrink-0 leading-none">•</span>
          <span>{li}</span>
        </li>
      ))}
    </ul>
  );
  if (b.link) return (
    <p className="my-3">
      <a href={b.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#B87333] font-bold hover:underline">
        {b.linkText || b.link} <ExternalLink className="h-4 w-4" />
      </a>
    </p>
  );
  return null;
};

export const WarrantyPolicy = ({ testid = 'warranty-policy' }) => (
  <section className="py-16 section-padding bg-white" data-testid={testid}>
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-2 text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">
        <ShieldCheck className="h-4 w-4" /> Warranty &amp; Support Policy
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Warranty &amp; Support Policy</h2>
      <p className="text-slate-500 mt-2 text-sm">LyphLine Freeze Dryer Retrofit Systems</p>

      {SECTIONS.map((s) => (
        <div key={s.id} id={s.id} className="mt-10 scroll-mt-28" data-testid={`${testid}-${s.id}`}>
          <h3 className="text-xl font-bold text-[#003456] border-b border-slate-200 pb-2">{s.title}</h3>
          {s.blocks.map((b, i) => <Block key={i} b={b} />)}
        </div>
      ))}

      {/* Contact */}
      <div className="mt-12 bg-[#003456] rounded-2xl p-8" data-testid={`${testid}-contact`}>
        <h3 className="text-2xl font-bold text-white">Contact LyphLine</h3>
        <p className="text-white/70 mt-2">For warranty claims, technical support, retrofit assistance, extended support options, or repair service:</p>
        <div className="mt-5 space-y-3 text-white/85">
          <a href={SUPPORT.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white transition-colors">
            <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0"><ShieldCheck className="h-4 w-4 text-[#CE8946]" /></span>
            Online Warranty Claims &amp; Technical Support — www.freezedryersupport.com
          </a>
          <a href="tel:+18882231435" className="flex items-center gap-3 hover:text-white transition-colors">
            <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0"><Phone className="h-4 w-4 text-[#CE8946]" /></span>
            Toll-Free: 1-888-223-1435
          </a>
          <a href="mailto:info@lyphlinefreezedry.com" className="flex items-center gap-3 hover:text-white transition-colors">
            <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0"><Mail className="h-4 w-4 text-[#CE8946]" /></span>
            info@lyphlinefreezedry.com
          </a>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0"><MapPin className="h-4 w-4 text-[#CE8946]" /></span>
            Service Location: Lethbridge, Alberta, Canada
          </div>
        </div>
        <p className="text-white/60 text-sm mt-5">When contacting support, please have your machine serial number, LyphLine retrofit information, and a description of the issue available.</p>
      </div>

      {/* Legal notice */}
      <div className="mt-8 border-t border-slate-200 pt-6">
        <h4 className="text-sm font-bold text-[#003456] uppercase tracking-wide">Important Legal Notice</h4>
        <p className="text-slate-500 text-sm leading-relaxed mt-2">
          This website policy is intended to explain LyphLine's general warranty and support practices. Specific transactions may also be governed by invoices, quotations, work orders, service agreements, extended protection agreements, or jurisdiction-specific warranty terms provided at or before purchase. Where this policy conflicts with a mandatory requirement of applicable law, the applicable law will control.
        </p>
      </div>
    </div>
  </section>
);

export default WarrantyPolicy;
